import { useState, useEffect, useRef } from "react";
import { X, Check, FileText } from "lucide-react";
import {
  requestTypeStatusColor,
  formatMDY,
  isFutureRequest,
  isPastWeekRequest,
} from "@utils/ptoHelpers";
import { PTOVersionHistoryDrawer } from "@components/user_pto/PTOVersionHistoryDrawer";

export function PTORequestDrawer({
  isOpen,
  request,
  onClose,
  approveMutation,
  rejectMutation,
  cancelMutation,
  userType,
}: {
  isOpen: boolean;
  request: any;
  onClose: () => void;
  approveMutation: any;
  rejectMutation: any;
  cancelMutation: any;
  userType?: string;
}) {
  const [rejectReason, setRejectReason] = useState("");
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: "approve" | "reject" | "cancel" | null;
  }>({ isOpen: false, action: null });
  const confirmModalRef = useRef<HTMLDivElement>(null);

  // Keyboard support and focus management for confirmation modal
  useEffect(() => {
    if (!confirmModal.isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setConfirmModal({ isOpen: false, action: null });
      }
    };

    // Focus modal on open
    if (confirmModalRef.current) {
      confirmModalRef.current.focus();
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [confirmModal.isOpen]);

  if (!isOpen || !request) return null;

  const handleApprove = async () => {
    try {
      const reason = rejectReason.trim() || undefined;
      await approveMutation.mutateAsync({ id: request.id, reason });
      setMessage({
        type: "success",
        text: "PTO request approved successfully!",
      });
      setTimeout(() => {
        setMessage(null);
        onClose();
      }, 2000);
    } catch (e: any) {
      setMessage({
        type: "error",
        text: e?.response.data.error || "Failed to approve request",
      });
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setMessage({ type: "error", text: "Please enter a note" });
      return;
    }
    try {
      await rejectMutation.mutateAsync({ id: request.id, reason });
      setMessage({
        type: "success",
        text: "PTO request rejected successfully!",
      });
      setTimeout(() => {
        setMessage(null);
        onClose();
      }, 2000);
    } catch (e: any) {
      setMessage({
        type: "error",
        text: e?.response.data.error || "Failed to reject request",
      });
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({ id: request.id });
      setMessage({
        type: "success",
        text: "PTO request cancelled successfully!",
      });
      setTimeout(() => {
        setMessage(null);
        onClose();
      }, 2000);
    } catch (e: any) {
      setMessage({
        type: "error",
        text: e?.response.data.error || "Failed to cancel request",
      });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.action) return;

    try {
      switch (confirmModal.action) {
        case "approve":
          await handleApprove();
          break;
        case "reject":
          await handleReject();
          break;
        case "cancel":
          await handleCancel();
          break;
      }
    } finally {
      setConfirmModal({ isOpen: false, action: null });
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex">
      <div
        className="tw-flex-[0.6] sm:tw-flex-1 tw-bg-black/40"
        onClick={onClose}
      />
      <div className="tw-w-full tw-max-w-xs sm:tw-max-w-md tw-bg-white dark:tw-bg-[#0D1117] tw-h-full tw-shadow-xl tw-border-l tw-border-gray-200 dark:tw-border-gray-700 tw-p-2 sm:tw-p-4 tw-overflow-auto">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 className="tw-text-sm sm:tw-text-lg tw-font-semibold">
            PTO Request
          </h3>
          <button
            className="tw-p-1 tw-text-gray-500 hover:tw-text-black"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="tw-space-y-1.5 sm:tw-space-y-2 tw-text-[10px] sm:tw-text-sm tw-border tw-border-gray-500 tw-rounded-md tw-px-1.5 sm:tw-px-2 tw-py-1.5 sm:tw-py-2">
          <div>
            <span className="tw-text-gray-500">Contractor:</span>{" "}
            <span>
              {request.contractor_name || request.user_email || request.user_id}
            </span>
          </div>
          <div>
            <span className="tw-text-gray-500">Dates:</span>{" "}
            <span>
              {formatMDY(request.start_date)} → {formatMDY(request.end_date)}
            </span>
          </div>
          <div>
            <span className="tw-text-gray-500">Hours:</span>{" "}
            <span>{request.hours_requested}</span>
          </div>
          <div>
            <span className="tw-text-gray-500">Request Type:</span>{" "}
            <span className="tw-capitalize">{request.request_type || "—"}</span>
          </div>
          <div>
            <span className="tw-text-gray-500">Balance at Request:</span>{" "}
            <span>{request.pto_balance_at_request ?? 0}</span>
          </div>
          <div>
            <span className="tw-text-gray-500">Balance after Request:</span>{" "}
            <span>{request.pto_balance_after ?? 0}</span>
          </div>
          {request.description && (
            <div>
              <span className="tw-text-gray-500">Description:</span>
              <div className="tw-mt-1 tw-whitespace-pre-wrap tw-border tw-rounded tw-p-2 tw-bg-gray-50 dark:tw-bg-[#0A1E2D]">
                {request.description}
              </div>
            </div>
          )}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-gray-500">Status:</span>
            {(() => {
              const { bg, text } = requestTypeStatusColor(
                request.status,
                request.request_type
              );
              return (
                <span
                  className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs ${bg} ${text}`}
                >
                  {request.status}
                </span>
              );
            })()}
          </div>
          {request.status_reason && (
            <div>
              <span className="tw-text-gray-500">Status note:</span>
              <div className="tw-mt-1 tw-whitespace-pre-wrap tw-border tw-rounded tw-p-2 tw-bg-gray-50 dark:tw-bg-[#0A1E2D]">
                {request.status_reason}
              </div>
            </div>
          )}
        </div>

        <div className="tw-mt-4 tw-border-t tw-pt-4">
          {message && (
            <div
              className={`tw-mb-3 tw-p-3 tw-rounded tw-text-xs sm:tw-text-sm ${
                message.type === "success"
                  ? "tw-bg-green-50 tw-text-green-800 tw-border tw-border-green-200"
                  : "tw-bg-red-50 tw-text-red-800 tw-border tw-border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          {request.status === "Pending" && (
            <>
              <label className="tw-block tw-text-xs tw-text-gray-600 tw-mb-1">
                Status note (optional)
              </label>
              <textarea
                className="tw-w-full tw-border tw-rounded-md tw-px-2 tw-py-2 tw-text-xs sm:tw-text-sm tw-bg-white dark:tw-bg-[#111827]"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Add a note if approving or rejecting"
              />
            </>
          )}

          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-1 sm:tw-gap-2">
            <button
              className="tw-inline-flex tw-items-center tw-gap-0.5 sm:tw-gap-1 tw-text-[10px] sm:tw-text-sm tw-text-green-700 tw-bg-green-100 hover:tw-bg-green-200 tw-border tw-border-green-200 tw-rounded tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
              onClick={() =>
                setConfirmModal({ isOpen: true, action: "approve" })
              }
              disabled={
                request.status !== "Pending" || approveMutation.isPending
              }
            >
              <Check size={16} /> Approve
            </button>
            <button
              className="tw-inline-flex tw-items-center tw-gap-0.5 sm:tw-gap-1 tw-text-[10px] sm:tw-text-sm tw-text-red-700 tw-bg-red-100 hover:tw-bg-red-200 tw-border tw-border-red-200 tw-rounded tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
              onClick={() =>
                setConfirmModal({ isOpen: true, action: "reject" })
              }
              disabled={
                request.status !== "Pending" || rejectMutation.isPending
              }
            >
              <X size={16} /> Reject
            </button>
            <button
              className="tw-inline-flex tw-items-center tw-gap-0.5 sm:tw-gap-1 tw-text-[10px] sm:tw-text-sm tw-text-orange-700 tw-bg-orange-100 hover:tw-bg-orange-200 tw-border tw-border-orange-200 tw-rounded tw-px-2 sm:tw-px-3 tw-py-1 sm:tw-py-1.5 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
              onClick={() =>
                setConfirmModal({ isOpen: true, action: "cancel" })
              }
              disabled={
                !(
                  request.status === "Approved" ||
                  request.status === "Balance_Overridden"
                ) ||
                (userType === "super_admin"
                  ? !(isFutureRequest(request) || isPastWeekRequest(request))
                  : !isFutureRequest(request)) ||
                cancelMutation.isPending
              }
            >
              <X size={16} /> Cancel
            </button>
            <button
              className="tw-inline-flex tw-items-center tw-gap-1 tw-text-blue-700 tw-bg-blue-50 hover:tw-bg-blue-100 tw-border tw-border-blue-200 tw-rounded tw-px-3 tw-py-1.5"
              onClick={() => setLogsDrawerOpen(true)}
            >
              <FileText size={16} /> Logs
            </button>
          </div>
        </div>
      </div>

      <PTOVersionHistoryDrawer
        isOpen={logsDrawerOpen}
        request={request}
        onClose={() => setLogsDrawerOpen(false)}
      />

      {confirmModal.isOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-black/50"
          onClick={() => setConfirmModal({ isOpen: false, action: null })}
        >
          <div
            ref={confirmModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            aria-describedby="confirm-modal-description"
            tabIndex={-1}
            className="tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-xl tw-max-w-xs tw-w-full tw-p-3 tw-max-h-[90vh] tw-overflow-y-auto tw-outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="confirm-modal-title"
              className="tw-text-sm tw-font-semibold tw-mb-2"
            >
              Confirm{" "}
              {confirmModal.action
                ? confirmModal.action.charAt(0).toUpperCase() +
                  confirmModal.action.slice(1)
                : ""}
            </h3>
            <p
              id="confirm-modal-description"
              className="tw-text-xs tw-text-gray-600 tw-mb-2"
            >
              Are you sure you want to {confirmModal.action} this PTO request?
            </p>
            <div className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded tw-p-2 tw-mb-2">
              <div className="tw-text-xs sm:tw-text-sm">
                <span className="tw-font-semibold">Contractor: </span>
                {request.contractor_name ||
                  request.user_email ||
                  request.user_id}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600 tw-mt-1">
                <span className="tw-font-semibold">Dates: </span>
                {formatMDY(request.start_date)} - {formatMDY(request.end_date)}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600">
                <span className="tw-font-semibold">Hours: </span>
                {request.hours_requested}
              </div>
            </div>
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-justify-end">
              <button
                className="tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-text-gray-700 hover:tw-bg-gray-100"
                onClick={() => setConfirmModal({ isOpen: false, action: null })}
              >
                Cancel
              </button>
              <button
                className={`tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-text-white ${
                  confirmModal.action === "approve"
                    ? "tw-bg-green-600 hover:tw-bg-green-700"
                    : confirmModal.action === "reject"
                    ? "tw-bg-red-600 hover:tw-bg-red-700"
                    : "tw-bg-orange-600 hover:tw-bg-orange-700"
                }`}
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
